import React, { useCallback, useEffect, useReducer, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import useForceUpdate from 'components/hooks/useForceUpdate';
import { isEmpty } from 'lodash';
import { shallowEqual, useSelector } from 'react-redux';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import {
  updateCompetencyTargetDate,
  updateEmployeeTargetRole,
  updateCompetencyProgressStatus,
  getLevelUpTargetDatesAndProgressStatuses,
} from 'services/growth/oneOnOne';

import { useLevelUpCompetencyData } from '../hooks/dataHooks';
import { LEVELUP_ACTIONS } from './actions';
import { LevelUpContextInitialState, LevelUpContext } from './index';
import { levelUpContextReducer } from './reducer';

export const LevelUpPageContextProvider = ({ children }) => {
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId, shallowEqual);

  const [forceUpdateValue, forceUpdate] = useForceUpdate();

  const queryClient = useQueryClient();

  // const { run, isLoading, isIdle } = useAsync();

  const [levelUpState, levelUpDispatch] = useReducer(levelUpContextReducer, LevelUpContextInitialState);

  const [filteredCompetencyTableData, setFilteredCompetencyTableData] = useState([]);

  // const setLevelUpDetails = useCallback(levelUpDetails => {
  //   levelUpDispatch({
  //     type: LEVELUP_ACTIONS.SET_LEVEL_UP_DETAILS,
  //     payload: levelUpDetails
  //   });
  // }, []);

  const openRoleSelectionModal = useCallback(({ isNewRoleSelection = false } = {}) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.OPEN_ROLE_SELECTION_MODAL,
      payload: { isNewRoleSelection },
    });
  }, []);

  const setIsManagerFlag = useCallback((isLoggedInEmployeeManager) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.SET_IS_MANAGER_FLAG,
      payload: isLoggedInEmployeeManager,
    });
  }, []);

  const closeRoleSelectionModal = useCallback(() => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.CLOSE_ROLE_SELECTION_MODAL,
      payload: null,
    });
  }, []);

  const changeSelectedRole = useCallback(({ isTargetRole }) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.CHANGE_SELECTED_ROLE,
      payload: { isTargetRole },
    });
  }, []);

  const handleCompetencyTargetDateChange = useCallback(
    ({ jobFunctionId, jobRoleId, competencyId, subCompetencyId, targetPeriodStart }) =>
      updateCompetencyTargetDate({
        employeeId: loggedInEmployeeId,
        jobFunctionId,
        jobRoleId,
        competencyId,
        subCompetencyId,
        targetPeriodStart,
      })
        .then((res) => {
          levelUpDispatch({
            type: LEVELUP_ACTIONS.UPDATE_COMPETENCY_TARGET_DATE,
            payload: {
              jobFunctionId,
              jobRoleId,
              competencyId,
              subCompetencyId,
              targetPeriodStart,
            },
          });
        })
        .catch((err) => console.error(err.message || 'failed to update competency target date')),
    [loggedInEmployeeId]
  );

  const handleRoleSelect = useCallback(
    async ({ jobFunctionId, jobRoleId }) => {
      try {
        const res = await updateEmployeeTargetRole({
          employeeId: loggedInEmployeeId,
          jobFunctionId,
          jobRoleId,
        });
        queryClient.invalidateQueries([REACT_QUERY_KEYS.LevelUpCompetencyData]);
      } catch (error) {
        console.error(error.message || 'failed to select role for level up');
      }
    },
    [loggedInEmployeeId]
  );

  const handleCompetencyProgressStatusChange = useCallback(
    ({ jobFunctionId, jobRoleId, competencyId, subCompetencyId, status }) =>
      updateCompetencyProgressStatus({
        loggedInEmployeeId,
        jobFunctionId,
        jobRoleId,
        competencyId,
        subCompetencyId,
        status,
      })
        .then((res) => {
          levelUpDispatch({
            type: LEVELUP_ACTIONS.UPDATE_COMPETENCY_PROGRESS_STATUS,
            payload: {
              jobFunctionId,
              jobRoleId,
              competencyId,
              subCompetencyId,
              status,
            },
          });
        })
        .catch((err) => console.error(err.message || 'failed to update competency progress status')),
    [loggedInEmployeeId]
  );

  const setFunctionAndRoleNotSet = useCallback((isSet) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.SET_FUNCTION_AND_ROLE_NOT_SET,
      payload: { isSet },
    });
  }, []);

  const { data: levelUpDetails, isLoading } = useLevelUpCompetencyData({
    employeeId: loggedInEmployeeId,
    targetRole: levelUpState.isTargetRoleSelected,
    changeSelectedRole,
    setFunctionAndRoleNotSet,
  });

  // useEffect(() => {
  //   run(fetchLevelUpCompetencyData());
  // }, [loggedInEmployeeId, levelUpState.isTargetRoleSelected, forceUpdateValue]);

  // const fetchLevelUpCompetencyData = useCallback(() => {
  //   return getLevelUpCompetencyData({
  //     employeeId: loggedInEmployeeId,
  //     targetRole: levelUpState.isTargetRoleSelected
  //   })
  //     .then(res => {
  //       changeSelectedRole({ isTargetRole: res?.tableData?.isTargetRole });
  //       const competenciesGroupedByCompetencyStatus = groupBy(
  //         res?.tableData?.competencies,
  //         'progress'
  //       );
  //       setLevelUpDetails({ ...res, competenciesGroupedByCompetencyStatus });
  //     })
  //     .catch(err => {
  //       if (err.status === 422) {
  //         setFunctionAndRoleNotSet(true);
  //       } else {
  //         console.error('Error fetching level up competency data : ', err);
  //       }
  //     });
  // }, [levelUpState.isTargetRoleSelected]);

  const setLevelUpTargetDates = useCallback((targetDates) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.SET_LEVEL_UP_TARGET_DATES,
      payload: targetDates,
    });
  }, []);

  const toggleCompetencyFilter = useCallback((filter) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.TOGGLE_COMPETENCY_FILTER,
      payload: { filter },
    });
  }, []);

  const setAllCompetencyFilter = useCallback(() => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.SET_ALL_COMPETENCY_FILTER,
    });
  }, []);

  // const setFilteredCompetencyTableData = useCallback(
  //   competencies => {
  //     levelUpDispatch({
  //       type: LEVELUP_ACTIONS.SET_FILTERED_COMPETENCY_TABLE_DATA,
  //       payload: { competencies, levelUpDetails }
  //     });
  //   },
  //   [levelUpDetails]
  // );

  const setLevelUpProgressStatuses = useCallback((progressStatuses) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.SET_LEVEL_UP_PROGRESS_STATUSES,
      payload: progressStatuses,
    });
  }, []);

  const setLevelUpRiskEstimationList = useCallback((riskEstimationList) => {
    levelUpDispatch({
      type: LEVELUP_ACTIONS.SET_LEVEL_UP_RISK_ESTIMATION_LIST,
      payload: riskEstimationList,
    });
  }, []);

  const setFilteredCompetencies = () => {
    const activeFiltersArray = Object.keys(levelUpState.competencyFilters)
      .map((k) => {
        if (levelUpState.competencyFilters[k]) {
          return k;
        }
      })
      .filter((k) => k);

    if (isEmpty(activeFiltersArray)) {
      const allCompetenciesArray = Object.keys(levelUpState.competencyFilters)
        .map((filter) => levelUpDetails?.competenciesGroupedByCompetencyStatus[filter])
        .filter((value) => value)
        .flat();
      setFilteredCompetencyTableData(allCompetenciesArray);
      return true;
    }

    const filteredCompetencies = activeFiltersArray
      .map((filter) => levelUpDetails?.competenciesGroupedByCompetencyStatus[filter])
      .filter((value) => value)
      .flat();

    if (filteredCompetencies && Array.isArray(filteredCompetencies)) {
      setFilteredCompetencyTableData(filteredCompetencies);
    }
  };

  useEffect(() => {
    // get Level up target dates and Progress Statuses, this is not employee specific
    getLevelUpTargetDatesAndProgressStatuses({ employeeId: loggedInEmployeeId })
      .then((res) => {
        const { targetDateList, progressList, riskEstimationList } = res;
        setLevelUpProgressStatuses(progressList);

        setLevelUpTargetDates(targetDateList);
        setLevelUpRiskEstimationList(riskEstimationList);
      })
      .catch((err) => console.error(err.message || 'failed to fetch level up details'));
  }, []);

  useEffect(() => {
    if (levelUpDetails?.tableData) {
      setFilteredCompetencies();
    }
  }, [levelUpState.competencyFilters, levelUpDetails?.tableData]);

  return (
    <LevelUpContext.Provider
      value={{
        state: {
          ...levelUpState,
          reporteeId: loggedInEmployeeId,
          isLoading,
          levelUpDetails,
          filteredCompetencyTableData,
        },
        // setLevelUpDetails,
        openRoleSelectionModal,
        closeRoleSelectionModal,
        handleCompetencyTargetDateChange,
        handleRoleSelect,
        handleCompetencyProgressStatusChange,
        changeSelectedRole,
        toggleCompetencyFilter,
        setAllCompetencyFilter,
        // fetchLevelUpCompetencyData
      }}
    >
      {children}
    </LevelUpContext.Provider>
  );
};
